import React, {useEffect, useRef, useState} from 'react';
import { Tween, update, Easing } from "@tweenjs/tween.js";
import {MapContext} from './MapContext';
import ModeSwitcher from '../liveLocation/ModeSwitcher';
import {calculateOffset, initializeMap} from '../../common/helper';
import Markers from './Markers';
import Circle from './Circle';
import Overlay from './Overlay';
import ZoomInControl from './ZoomInControl';
import {useDispatch, useSelector} from 'react-redux';
import {selectEventsData, selectPseudoFlow, selectTrackingStatus} from '../onboarding/onboardingSlice';
import FakeMarkers from './FakeMarkers';
import {sendAnalyticsEvents} from '../../analytics/common';
import {EVENTS} from '../../analytics/events';
import {setPopupShow} from './mapSlice';


const StyledArray = [
    {
        "elementType": "geometry",
        "stylers": [
            {
                "color": "#f5f5f5"
            }
        ]
    },
    {
        "elementType": "labels.icon",
        "stylers": [
            {
                "visibility": "off"
            }
        ]
    },
    {
        "elementType": "labels.text.fill",
        "stylers": [
            {
                "color": "#616161"
            }
        ]
    },
    {
        "elementType": "labels.text.stroke",
        "stylers": [
            {
                "color": "#f5f5f5"
            }
        ]
    },
    {
        "featureType": "administrative.land_parcel",
        "elementType": "labels.text.fill",
        "stylers": [
            {
                "color": "#bdbdbd"
            }
        ]
    },
    {
        "featureType": "poi",
        "elementType": "geometry",
        "stylers": [
            {
                "color": "#eeeeee"
            }
        ]
    },
    {
        "featureType": "poi",
        "elementType": "labels.text.fill",
        "stylers": [
            {
                "color": "#757575"
            }
        ]
    },
    {
        "featureType": "poi.park",
        "elementType": "geometry",
        "stylers": [
            {
                "color": "#e5e5e5"
            }
        ]
    },
    {
        "featureType": "poi.park",
        "elementType": "labels.text.fill",
        "stylers": [
            {
                "color": "#9e9e9e"
            }
        ]
    },
    {
        "featureType": "road",
        "elementType": "geometry",
        "stylers": [
            {
                "color": "#ffffff"
            }
        ]
    },
    {
        "featureType": "road.arterial",
        "elementType": "labels.text.fill",
        "stylers": [
            {
                "color": "#757575"
            }
        ]
    },
    {
        "featureType": "road.highway",
        "elementType": "geometry",
        "stylers": [
            {
                "color": "#dadada"
            }
        ]
    },
    {
        "featureType": "road.highway",
        "elementType": "labels.text.fill",
        "stylers": [
            {
                "color": "#616161"
            }
        ]
    },
    {
        "featureType": "road.local",
        "elementType": "labels.text.fill",
        "stylers": [
            {
                "color": "#9e9e9e"
            }
        ]
    },
    {
        "featureType": "transit.line",
        "elementType": "geometry",
        "stylers": [
            {
                "color": "#e5e5e5"
            }
        ]
    },
    {
        "featureType": "transit.station",
        "elementType": "geometry",
        "stylers": [
            {
                "color": "#eeeeee"
            }
        ]
    },
    {
        "featureType": "water",
        "elementType": "geometry",
        "stylers": [
            {
                "color": "#c9c9c9"
            }
        ]
    },
    {
        "featureType": "water",
        "elementType": "labels.text.fill",
        "stylers": [
            {
                "color": "#9e9e9e"
            }
        ]
    }
]


const Map = ({lat, lng, database}) => {
    const googleMapRef = useRef();
    const [googleMap, setGoogleMap] = useState(null);
    const pseudoTrackingDone = useSelector(selectTrackingStatus);
    const isPseudoFlow = useSelector(selectPseudoFlow);
    const {newLat, newLng} = calculateOffset(lat, lng);
    const dispatch = useDispatch();
    const amplitudeEventData = useSelector(selectEventsData);

    useEffect(() => {
        initializeMap(lat, lng, createGoogleMap);
    }, []);

    useEffect(() => {
        googleMap && googleMap.addListener('click', () => dispatch(setPopupShow(false)))
    }, [googleMap])

    const animateCamera = () => {
         new Tween(cameraOptions) // Create a new tween that modifies 'cameraOptions'.
            .to({ tilt: 65, heading: 90, zoom: 13 }, 10000) // Move to destination in 10 second.
            .easing(Easing.Quadratic.Out) // Use an easing function to make the animation smooth.
            .onUpdate(() => {
                googleMap.moveCamera(cameraOptions);
            })
            .start();

        function animate(time) {
            requestAnimationFrame(animate);
            update(time);
        }

        requestAnimationFrame(animate);

        sendAnalyticsEvents(EVENTS.pseudoLocateAnimation, amplitudeEventData);
    }

    useEffect(() => {
        if (googleMap && pseudoTrackingDone) {
            animateCamera();
        }
    }, [googleMap, pseudoTrackingDone])


    const cameraOptions = {
        tilt: 0,
        heading: 0,
        zoom: isPseudoFlow ? 3 : 17,
        center: {lat: newLat, lng: newLng},
    };

    const createGoogleMap = () => {

        const mapOptions = {
            ...cameraOptions,
            fullscreenControl: false,
            streetViewControl: false,
            styles: isPseudoFlow ? [] : StyledArray,
            mapTypeControl: false,
            mapTypeId: 'roadmap',
            disableDefaultUI: true,
            mapId: isPseudoFlow ? '15431d2b469f209e' : null
        }

        const map = new window.google.maps.Map(googleMapRef.current, mapOptions);
        setGoogleMap(map);
    };


    const state = {
        googleMap,
        lat,
        lng,
        database
    }


    return (
        <MapContext.Provider value={state}>
            <div
                ref={googleMapRef}
                id="google-map"
                style={{width: '100%', height: '100%'}}/>
              {googleMap && <Markers/>}
              {(googleMap && isPseudoFlow) && <FakeMarkers/>}

              {googleMap && <Circle/>}
              {(googleMap && !isPseudoFlow) && <Overlay/>}
              <ModeSwitcher map={googleMap} topPosition={'16px'}/>
              <ZoomInControl map={googleMap}/>
        </MapContext.Provider>

    )
};

export default Map;
